/* eslint-disable valid-jsdoc */
import {
  cloneData, cloneDataOrNull, DataModel, DataModelBuilder, generateUniqueDatabaseId,
  isSameData, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { PhoneNumber } from '../phone-number.js';
import { BirthDate } from '../birth-date.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { CustomerProfile, CustomerProfileStatus } from '../customer-profile.js';
import { extractFirstAndLastName } from '../../../utility/general-utility.js';
import { TrueTime } from '../../../time/true-time.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { ComponentList } from '../../../components/core/component-list.js';
import { BillingProfile } from '../billing-profile.js';
import { CustomerLocation } from '../customer-location.js';
import { BillingDetailsComponent } from '../../../components/customer-account/billing-details-component.js';
import { CustomerLocationComponent } from '../../../components/customer-profile/customer-location-component.js';
import { Result } from '../../../utility/result.js';

export class CartCustomerDto extends DataModel {
  static readonly TYPE_ID: string = '128e055cf6ea47c28e97436a113f7f06';

  private constructor(
    public readonly name: string | null,
    public readonly phoneNumber: PhoneNumber | null,
    public readonly email: string | null,
    public readonly dayOfBirth: BirthDate | null,
    public readonly billingDetails: BillingProfile | null,
    public readonly location: CustomerLocation | null,
  ) {
    super();
  }

  get $typeId(): string {
    return CartCustomerDto.TYPE_ID;
  }

  /**
   * @deprecated Only used to migrate older versions of [OnlineCartDto].
   */
  static fromCustomer(customer: CustomerProfile | null): Result<CartCustomerDto, string> {
    if(customer == null) {
      return Result.error('customer is null');
    }

    const locationComponent = customer.findComponentByTypeId<CustomerLocationComponent>(CustomerLocationComponent.TYPE_ID);
    const billingComponent = customer.findComponentByTypeId<BillingDetailsComponent>(BillingDetailsComponent.TYPE_ID);

    if(locationComponent == null) {
      return Result.error(`component missing: ${CustomerLocationComponent.TYPE_ID}`);
    }
    if(billingComponent == null) {
      return Result.error(`component missing: ${BillingDetailsComponent.TYPE_ID}`);
    }
    if(billingComponent.size <= 0) {
      return Result.error(`no billing details found`);
    }
    if(billingComponent.size > 1) {
      return Result.error(`multiple billing profiles found`);
    }

    const dto = new CartCustomerDto(
        customer.formattedName ?? 'Unknown user',
        cloneDataOrNull(customer.phoneNumber),
        customer.email,
        customer.dayOfBirth != null ? BirthDate.fromDayOfYear(customer.dayOfBirth) : null,
        billingComponent.getAnyBillingProfile()!,
        locationComponent.location,
    );

    return Result.ok(dto);
  }

  static online(opts: {
    name: string,
    phoneNumber: PhoneNumber | null,
    email: string | null,
    dayOfBirth: BirthDate | null,
    billingDetails: BillingProfile | null,
    location: CustomerLocation,
  }) {
    return new CartCustomerDto(
        opts.name,
        opts.phoneNumber,
        opts.email,
        opts.dayOfBirth,
        opts.billingDetails,
        opts.location,
    );
  }

  static offline(opts: {
    name?: string | null,
    phoneNumber?: PhoneNumber | null,
    email?: string | null,
    dayOfBirth?: BirthDate | null,
    billingDetails?: BillingProfile | null,
    location?: CustomerLocation | null,
  }) {
    return new CartCustomerDto(
        opts.name ?? null,
        opts.phoneNumber ?? null,
        opts.email ?? null,
        opts.dayOfBirth ?? null,
        opts.billingDetails ?? null,
        opts.location ?? null,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): CartCustomerDto {
    return new CartCustomerDto(
        MapUtils.getStringOrNull(map, 'name'),
        PhoneNumber.decode(MapUtils.getStringOrNull(map, 'phoneNumber')),
        MapUtils.getStringOrNull(map, 'email'),
        context.fromDataOrNull<BirthDate>(MapUtils.getRecordOrNull(map, 'dayOfBirth')),
        context.fromDataOrNull<BillingProfile>(MapUtils.getRecordOrNull(map, 'billingDetails')),
        context.fromDataOrNull<CustomerLocation>(MapUtils.getRecordOrNull(map, 'location')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'name': this.name,
      'phoneNumber': this.phoneNumber?.encode() ?? null,
      'email': this.email,
      'dayOfBirth': context.toDataOrNull(this.dayOfBirth),
      'billingDetails': context.toDataOrNull(this.billingDetails),
      'location': context.toDataOrNull(this.location),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: CartCustomerDto): boolean {
    if(this === other) {
      return true;
    }

    return other.name === this.name &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        other.email === this.email &&
        isSameData(other.dayOfBirth, this.dayOfBirth) &&
        isSameData(other.billingDetails, this.billingDetails) &&
        isSameData(other.location, this.location);
  }

  copyWith({
    name = this.name,
    phoneNumber = cloneDataOrNull(this.phoneNumber),
    email= this.email,
    dayOfBirth = cloneDataOrNull(this.dayOfBirth),
    billingDetails = cloneDataOrNull(this.billingDetails),
    location = cloneDataOrNull(this.location),
  }: {
    name?: string | null,
    phoneNumber?: PhoneNumber | null,
    email?: string | null,
    dayOfBirth?: BirthDate | null,
    billingDetails?: BillingProfile | null,
    location?: CustomerLocation | null,
  } = {}): this {
    return new CartCustomerDto(
        name,
        phoneNumber,
        email,
        dayOfBirth,
        billingDetails,
        location,
    ) as this;
  }

  toCustomer(opts: {
    businessRef: BusinessModuleRef,
    userId: string | null,
    isOnlineCustomer: boolean,
    uniqueId?: string,
  }): Result<CustomerProfile, string> {
    const { firstName, lastName } = extractFirstAndLastName(this.name ?? '');
    if(opts.isOnlineCustomer && firstName == null && lastName == null) {
      return Result.error(`name not valid: ${this.name}`);
    }

    const components = new ComponentList();
    if(this.location != null) {
      components.addComponent(new CustomerLocationComponent(cloneData(this.location)));
    }
    if(this.billingDetails != null) {
      components.addComponent(BillingDetailsComponent.fromProfile(cloneData(this.billingDetails)));
    }

    const customerProfile = CustomerProfile.create({
      uniqueId: opts.uniqueId ?? generateUniqueDatabaseId(),
      firstName: firstName,
      lastName: lastName,
      creationDate: TrueTime.now(),
      isOnlineCustomer: opts.isOnlineCustomer,
      businessRef: opts.businessRef,
      phoneNumber: cloneDataOrNull(this.phoneNumber),
      email: this.email,
      photo: null,
      pushNotificationToken: null,
      userId: opts.userId,
      language: null,
      components: components,
      dayOfBirth: this.dayOfBirth?.toDayOfYear() ?? null,
      status: CustomerProfileStatus.Live,
    });

    return Result.ok(customerProfile);
  }
}

export class CartCustomerDtoBuilder extends DataModelBuilder<CartCustomerDto> {
  build(context: SerializationContext, map: SerializedData): CartCustomerDto {
    return CartCustomerDto.fromMap(context, map);
  }
}
